<template>
  <div>
    <q-list bordered separator>
      <q-item v-ripple v-for="(task, index) in tasks" :key="index">
        <q-item-section avatar>
          <q-btn round color="primary">
            <q-avatar v-if="task.icon">
              <img class="q-pa-sm" :src="task.icon" width="40px" />
            </q-avatar>
            <q-avatar v-else>
              <q-icon
                class="q-pa-sm icon-position"
                name="schedule"
                width="40px"
                style="top: -4px"
              />
            </q-avatar>
          </q-btn>
        </q-item-section>
        <q-item-section>
          <q-item-label>{{ task.title }}</q-item-label>
          <div>
            <q-item-label>
              "<u>{{ task.subtitle }}</u
              >" : <span>{{ task.getDescription }}</span>
            </q-item-label>
          </div>
        </q-item-section>
        <q-item-section side top class="text-center">
          <q-item-label caption>{{ task.date }}</q-item-label>
          <q-chip
            dense
            outline
            :color="task.status.color"
            class="q-mx-none"
            style="font-size: 11px"
          >
            {{ task.status.label }}
          </q-chip>
        </q-item-section>
      </q-item>
      <q-page-scroller
        position="bottom-right"
        :scroll-offset="150"
        :offset="[18, 18]"
        reverse
      >
        <q-btn round icon="keyboard_arrow_down" color="accent" />
      </q-page-scroller>
    </q-list>
    <div class="row justify-center q-mt-md" v-if="tasks.length > 0">
      <q-btn
        class="button-width full-width"
        color="primary"
        @click="syncTmpItemsJob"
      >
        {{ $t('sync.retrySend') }}
      </q-btn>
    </div>
    <h5
      v-else
      class="text-center absolute-center full-width text-grey-7 q-pb-xl"
    >
      {{ $t('sync.finish') }}, <br />{{ $t('sync.exitPage') }}
    </h5>
    <div class="row justify-center" v-if="tasks.length > 0">
      <div
        class="text-center help-message q-mt-xl q-mb-md q-mx-md bg-grey-3 q-pa-sm"
      >
        * {{ $t('sync.sendIssueError') }}
        <span
          class="cursor-pointer text-primary"
          @click="showSendFailedRequests = true"
          >{{ $t('sync.contactDevelopers') }}</span
        >
      </div>
    </div>
    <q-dialog v-model="showSendFailedRequests" maximized>
      <send-failed-requests @closeModal="showSendFailedRequests = false" />
    </q-dialog>
  </div>
</template>

<script>
import {
  defineComponent,
  inject,
  computed,
  onMounted,
  onBeforeUnmount,
  ref
} from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import SendFailedRequests from 'src/components/modals/SendFailedRequests.vue'

export default defineComponent({
  components: {
    SendFailedRequests
  },
  setup() {
    const $DB = inject('$DB')
    const $CRON = inject('$cron')
    const $helpers = inject('$helpers')
    const $store = useStore()
    const { t } = useI18n({ useScope: 'global' })

    const storedTasks = ref([])
    const tasks = computed(() => {
      return storedTasks.value.map((task) => {
        return {
          title: ['close', 'talk', 'code'].includes(task.event)
            ? `${t('common.issue')} n° ${task._id.split(":")[0]}`
            : task.place.name,
          subtitle: getTaskType(task),
          getDescription: getDescription(task),
          date: task.scheduledAt,
          status: translateStatus(task),
          icon: task.img
        }
      })
    })

    const showSendFailedRequests = ref(false)

    const transformStatus = (status) => {
      switch (status) {
        case 'IN_PROGRESS':
          return 'ongoing'
        case 'PENDING':
          return 'pending'
        default:
          return 'failed'
      }
    }

    const translateStatus = (task) => {
      const statusIssue = task.wip ? 'IN_PROGRESS' : 'PENDING'
      const statusTask = task.hasOwnProperty('status')
        ? task.status
        : statusIssue
      return $helpers.statusTaskTranslation(transformStatus(statusTask))
    }

    const getTaskType = (task) => {
      return task.event ? t('common.issue') : t('common.audit')
    }

    const getDescription = (task) => {
      let messageTalk = ''
      if (task.event === 'talk') {
        messageTalk = t('sync.sendingTalk')
      } else if (task.event === 'close') {
        messageTalk = t('sync.sendingClose')
      } else if (task.event === 'code') {
        messageTalk = t('sync.sendingCode')
      }
      return task.hasOwnProperty('message')
        ? task.message
        : task.hasOwnProperty('checklist')
        ? task.checklist.name
        : messageTalk
    }

    const loadTasksList = async () => {
      storedTasks.value = await $DB.getTasksList($store)
      if (storedTasks.value.length === 0 && intervalHandler.value) {
        clearInterval(intervalHandler.value)
        intervalHandler.value = null
      }
    }

    const syncTmpItemsJob = () => {
      $CRON.stop('syncTmpItemsJob')
      $CRON.start('syncTmpItemsJob')
    }

    const intervalHandler = ref(null)
    onMounted(async () => {
      await loadTasksList()
      intervalHandler.value = setInterval(loadTasksList, 5000)
    })

    onBeforeUnmount(() => {
      if (intervalHandler.value) {
        clearInterval(intervalHandler.value)
        intervalHandler.value = null
      }
    })

    return {
      tasks,
      showSendFailedRequests,
      translateStatus,
      getTaskType,
      getDescription,
      syncTmpItemsJob
    }
  }
})
</script>

<style scoped lang="stylus">
.button-width {
  max-width: 300px
}

.text-underlined {
  text-decoration: underline black
}

.help-message {
  max-width: 300px
}
</style>
